<template>
	<div class="menu-container">
		<ClassicMenuItem
			v-for="menuItem in leftMenuItems"
			:data-id="menuItem.key"
			:style="{ visibility: menuItem.hidden ? 'hidden' : 'visible' }"
			class="observable"
			:key="menuItem.key"
			:menuItem="menuItem"
			@click="onClick(menuItem)"
		/>
		<div class="flex-grow"></div>
		<ClassicMenuItem
			v-for="menuItem in rightMenuItems"
			:data-id="menuItem.key"
			:style="{ visibility: menuItem.hidden ? 'hidden' : 'visible' }"
			class="observable"
			:key="menuItem.key"
			:menuItem="menuItem"
			@click="onClick(menuItem)"
		/>
	</div>
	<div v-if="menuIconVisible" class="menu-icon-container" @click="menuToggle">
		<DoubleRightOutlined :class="['menu-icon', { 'menu-icon-visible': menuOpened }]" />
		<MenuOutlined :class="['menu-icon', { 'menu-icon-visible': !menuOpened }]" />
	</div>
	<div :class="popMenuOverlayClasses" @click="menuToggle" />
	<div :class="popMenuContentClasses">
		<ClassicMenuItem
			v-for="menuItem in popMenuItems"
			:key="menuItem.key"
			:menuItem="menuItem"
			@click="onClick(menuItem)"
		/>
	</div>
</template>

<script>
import { ref, computed, toRef, onMounted, onBeforeUnmount, watch, nextTick } from 'vue';

import { MenuOutlined, DoubleRightOutlined } from '@ant-design/icons-vue';

import { useRoute } from 'vue-router';

import ClassicMenuItem from '@/components/common/ClassicMenuItem';

const REWRITES = {
	'add-rover': 'rovers',
	'rover': 'rovers',
	'rover-change-tariff': 'rovers',
	'payment-page': 'payments',
	'activation-required': 'registration',
}

export default {
	components: {
		ClassicMenuItem, MenuOutlined, DoubleRightOutlined,
	},
	emits: ['select'],
	props: {
		selectedKey: {
			type: String,
			required: false,
		},
		items: {
			type: Array,
			required: true,
		},
		rightKeys: {
			type: Array,
			required: false,
			default: () => [],
		},
	},
	setup(props, { emit }) {
		const selectedKeyRef = toRef(props, 'selectedKey');
		const itemsRef = toRef(props, 'items');
		const rightKeysRef = toRef(props, 'rightKeys');

		const checkSelected = itemKey => {
			if (selectedKeyRef.value === itemKey) {
				return true;
			}
			if (selectedKeyRef.value in REWRITES) {
				return REWRITES[selectedKeyRef.value] === itemKey;
			}
			return false;
		};

		const hiddenKeys = ref([]);
		const menuItems = computed(() => itemsRef.value.map(item => ({
			...item,
			selected: checkSelected(item.key),
			hidden: hiddenKeys.value.includes(item.key),
		})));
		const leftMenuItems = computed(() => menuItems.value.filter(item => !rightKeysRef.value.includes(item.key)));
		const rightMenuItems = computed(() => menuItems.value.filter(item => rightKeysRef.value.includes(item.key)));
		const popMenuItems = computed(() => menuItems.value.filter(item => item.hidden));

		const onClick = item => emit('select', item.key);

		const handleIntersect = (entries, observer) => {
			entries.forEach(entry => {
				if (entry.intersectionRatio < 1) {
					const keys = hiddenKeys.value.concat([entry.target.getAttribute('data-id')]);
					hiddenKeys.value = [...new Set(keys)];
				} else {
					hiddenKeys.value = hiddenKeys.value.filter(key => key !== entry.target.getAttribute('data-id'));
				}
			});
		}
		let observer;
		const resetObserver = () => {
			if (observer != null) {
				observer.disconnect();
			}
			const options = {
				root: document.querySelector('.menu-container'),
				rootMargin: '0px',
				threshold: [0, 1],
			};
			hiddenKeys.value = [];
			observer = new IntersectionObserver(handleIntersect, options);
			document.querySelectorAll('.observable').forEach(el => observer.observe(el));
		};
		onMounted(resetObserver);
		onBeforeUnmount(() => {
			observer.disconnect();
		})
		watch(itemsRef, () => {
			nextTick(resetObserver);
		});

		const menuIconVisible = computed(() => hiddenKeys.value.length > 0);
		watch(menuIconVisible, value => {
			if (!value) {
				menuOpened.value = false;
			}
		})
		const menuOpened = ref(false);
		const menuToggle = () => menuOpened.value = !menuOpened.value;
		const route = useRoute();
		watch(route, () => {
			menuOpened.value = false;
		});
		const popMenuOverlayClasses = computed(() => ['pop-menu-overlay', { 'pop-menu-overlay-visible': menuOpened.value }]);
		const popMenuContentClasses = computed(() => ['pop-menu-content', { 'pop-menu-content-visible': menuOpened.value }]);
		return {
			leftMenuItems,
			rightMenuItems,
			popMenuItems,
			onClick,

			menuIconVisible,
			menuOpened,
			menuToggle,
			popMenuOverlayClasses,
			popMenuContentClasses,
		}
	},
}
</script>

<style scoped>
.menu-container {
	display: flex;
	overflow: hidden;
	flex-grow: 1;
}

.menu-icon-container {
	position: relative;
	width: 40px;
	height: 64px;
	box-sizing: content-box;
	flex-shrink: 0;
	cursor: pointer;
}

.menu-icon {
	position: absolute;
	top: 20px;
	line-height: 1.5715;
	font-size: 16px;
	color: hsla(0, 0%, 100%, .65);
	opacity: 0;
	transition: opacity .3s;
}

.menu-icon-visible {
	opacity: 1;
}

.pop-menu-overlay {
	z-index: 100;
	position: fixed;
	top: 64px;
	right: -100%;
	width: 100%;
	bottom: 0;
	opacity: 0;
	background-color: rgba(0, 0, 0, .2);
	transition: opacity .3s ease-out;
}

.pop-menu-overlay-visible {
	right: 0;
	opacity: 1;
}

.pop-menu-content {
	z-index: 101;
	position: fixed;
	top: 64px;
	right: -240px;
	width: min(230px, 100%);
	height: calc(100% - 64px);
	background-color: rgba(0, 13, 24, 0.6);
	border-left: 1px solid rgba(255, 255, 255, 0.5);
	backdrop-filter: blur(6px);
	overflow-y: auto;
	display: flex;
	flex-direction: column;
	box-shadow: -5px 0 5px rgba(0, 0, 0, 0.25);
	transition: right .3s ease-out;
}

.pop-menu-content-visible {
	right: 0;
}
</style>
